'use client';

import { Edit, AlertTriangle } from '@velocity-exchange/icons';
import {
	BigNum,
	ZERO,
	PRICE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	OrderType,
	PositionDirection,
} from '@velocity-exchange/sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import React from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useTriggerVersusLiqWarning } from 'src/hooks/Liquidations/useTriggerVersusLiqWarning';
import { AccountOpenPosition } from 'src/@types/positions';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import { useTpSlOrdersByMarket } from 'src/hooks/useTpSlOrdersByMarket';
import { cj } from 'src/utils/classNames';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { compareDriftProps } from 'src/utils/compareProps';
import NumLib from 'src/utils/NumLib';
import {
	ENUM_UTILS,
	matchEnum,
	calculatePotentialProfit,
	getOrderLabelFromOrderDetails,
} from '@velocity-exchange/common';
import Divider from 'src/components/primitives/Divider/Divider';
import { getColorVar } from 'src/utils/theme/colorVars';
import { signDependentValue } from 'src/utils/signDependentValue';

type TakeProfitStopLossButtonProps = {
	position: AccountOpenPosition;
	handleOpenTpSlModal: () => void;
};

export const TakeProfitStopLossButton = React.memo(
	function TakeProfitStopLossButtonMemo(props: TakeProfitStopLossButtonProps) {
		const isMobile = useIsMobileScreenSize();
		const setStore = useDriftStore((s) => s.set);
		const marketIndex = props.position.marketIndex;

		const isLong = props.position.direction === 'long';

		const orders = useTpSlOrdersByMarket(marketIndex, isLong);

		const calculateEstimatedPnl = (
			exitPrice: BigNum,
			exitBaseSize: BigNum,
			isMarketOrder: boolean
		): BigNum | null => {
			try {
				const result = calculatePotentialProfit({
					currentPositionSize: BigNum.from(
						props.position.baseSize.abs(),
						BASE_PRECISION_EXP
					),
					currentPositionDirection: ENUM_UTILS.toObj(props.position.direction),
					tradeDirection: isLong
						? PositionDirection.SHORT
						: PositionDirection.LONG,
					exitBaseSize,
					exitPrice: BigNum.from(exitPrice.val, PRICE_PRECISION_EXP),
					currentPositionEntryPrice: BigNum.from(
						props.position.entryPrice,
						PRICE_PRECISION_EXP
					),
					isMarketOrder,
					takerFeeBps: 0,
				});
				return result.estimatedProfit;
			} catch {
				return null;
			}
		};
		const handleViewMultiple = (e: React.MouseEvent<HTMLButtonElement>) => {
			// Prevent click from also opening up position details side drawer.
			e.stopPropagation();

			setStore((s) => {
				s.tradePageTable.currentTab = 'openOrders';
			});
		};

		const handleAddTpOrSl = (e: React.MouseEvent<HTMLDivElement>) => {
			// Prevent click from also opening up position details side drawer.
			e.stopPropagation();

			props.handleOpenTpSlModal();
		}; // if we check for any triggers past liquidation, that means on a long we take the lowest, on a short we take the highest
		const triggerPrice =
			(isLong
				? (orders.takeProfitPositionOrder?.triggerPrice.val ?? ZERO)
				: orders.stopLossPositionOrder?.triggerPrice.val) ?? ZERO;

		const warning = useTriggerVersusLiqWarning(
			triggerPrice,
			props.position.liqPrice,
			props.position.direction,
			true
		);
		const shouldShowWarning = !!warning;

		const hasPartialOrders =
			orders.takeProfitPartialOrders.length > 0 ||
			orders.stopLossPartialOrders.length > 0;

		const buttonHTML = (
			<div
				className={cj(
					'flex leading-2xs',
					!isMobile
						? 'flex-col items-start'
						: 'w-full items-center justify-between'
				)}
				onClick={handleAddTpOrSl}
			>
				<div
					className={cj(
						'group flex items-center justify-center gap-x-xs',
						shouldShowWarning && 'text-star-yellow'
					)}
				>
					{orders.takeProfitPositionOrder ? (
						<NumberDisplay
							value={BigNum.from(
								orders.takeProfitPositionOrder.triggerPrice.val,
								PRICE_PRECISION_EXP
							)}
							type="currency"
							precision="trade"
							className="typo-body-xs sm:typo-label-xs"
						/>
					) : (
						<span>-</span>
					)}
					<span>/</span>
					{orders.stopLossPositionOrder ? (
						<NumberDisplay
							value={BigNum.from(
								orders.stopLossPositionOrder.triggerPrice.val,
								PRICE_PRECISION_EXP
							)}
							type="currency"
							precision="trade"
							className="typo-body-xs sm:typo-label-xs"
						/>
					) : (
						<span>-</span>
					)}
					{!isMobile && (
						<IconButton
							size="sm"
							variant="tertiary"
							className={cj(
								// If the TP/SL prices are long, this can get pushed into the next column.
								// Adding a `z-1` ensures that it remains clickable when this happens.
								'z-1',
								hasPartialOrders ? '-mb-0 pb-0' : '-m-2 p-2',
								shouldShowWarning ? 'text-star-yellow' : 'text-text-secondary'
							)}
							icon={Edit}
						/>
					)}
				</div>
				{(orders.takeProfitPartialOrders.length > 0 ||
					orders.stopLossPartialOrders.length > 0) && (
					<Tooltip
						disabled={isMobile}
						decoration="none"
						contentClassName="max-w-none"
						content={
							<div className="flex min-w-[472px] flex-col gap-y-sm typo-body-xs">
								<div className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr] text-text-default">
									<span>Order Type</span>
									<span>Filled / Size</span>
									<span>Trigger</span>
									<span>Price</span>
									<span className="text-end">P&L</span>
								</div>
								<Divider className="bg-text-tertiary" />
								{[
									...orders.takeProfitPartialOrders,
									...orders.stopLossPartialOrders,
								].map((order, index) => {
									const exitPrice =
										(matchEnum(order.orderType, OrderType.TRIGGER_LIMIT) ||
											matchEnum(order.orderType, OrderType.LIMIT)) &&
										order.price
											? order.price
											: order.triggerPrice;

									const estimatedPnl = calculateEstimatedPnl(
										exitPrice,
										order.baseAssetAmount,
										matchEnum(order.orderType, OrderType.TRIGGER_MARKET)
									);
									const pnlNum = estimatedPnl?.toNum() ?? 0;

									return (
										<div
											key={`order-${index}`}
											className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr]"
										>
											<span className="text-text-default">
												{getOrderLabelFromOrderDetails(order)}
											</span>
											<span className="text-text-default">
												{NumLib.formatNum.toBaseDisplay(
													order.baseAssetAmountFilled.toNum()
												)}{' '}
												/{' '}
												{NumLib.formatNum.toBaseDisplay(
													order.baseAssetAmount.toNum()
												)}
											</span>
											<span className="text-text-default">
												<NumberDisplay
													value={BigNum.from(
														order.triggerPrice.val,
														PRICE_PRECISION_EXP
													)}
													type="currency"
													precision="trade"
													trimZeroes
												/>
											</span>
											<span className="text-text-default">
												{matchEnum(order.orderType, OrderType.TRIGGER_LIMIT) ||
												matchEnum(order.orderType, OrderType.LIMIT) ? (
													<NumberDisplay
														value={BigNum.from(
															order.price.val,
															PRICE_PRECISION_EXP
														)}
														type="currency"
														precision="trade"
														trimZeroes
													/>
												) : (
													<span className="ml-2">--</span>
												)}
											</span>
											<span
												className={cj(
													'text-end',
													signDependentValue(pnlNum, {
														positive: 'text-text-positive-green-button',
														negative: 'text-text-negative-red-button',
														zero: 'text-text-default',
													})
												)}
											>
												<NumberDisplay
													value={estimatedPnl}
													type="currency_signed"
													fallback="--"
												/>
											</span>
										</div>
									);
								})}
							</div>
						}
					>
						<TextButton
							variant="tertiary"
							size="sm"
							className="sm:mt-1"
							onClick={handleViewMultiple}
						>
							View partial orders
						</TextButton>
					</Tooltip>
				)}
			</div>
		);

		return !shouldShowWarning ? (
			buttonHTML
		) : (
			<Tooltip contentClassName="text-star-yellow" content={warning}>
				{buttonHTML}
				{!isMobile && (
					<AlertTriangle
						className="ml-1 flex xs:h-4 xs:w-4 sm:h-3 sm:w-3"
						color={getColorVar('star-yellow')}
					/>
				)}
			</Tooltip>
		);
	},
	compareDriftProps
);
